import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Drawer,
  Group,
  Loader,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import moment from "moment";
import React from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaClock,
  FaColumns,
  FaCommentDots,
  FaEdit,
  FaExclamationTriangle,
  FaFilter,
  FaImages,
  FaRegCalendarCheck,
  FaUserTie,
} from "react-icons/fa";
import InfiniteScroll from "react-infinite-scroll-component";
import { useApp } from "../../contexts/app.context";
import { GET_ALL_JOBS, GET_ALL_TASKS_DESTINATIONS, GET_JOB_DETAILS, GET_JOB_STATUS_OPTIONS, SAVE_JOB, SAVE_JOB_STATUS } from "../../services/jobs";

type ViewMode = "kanban" | "timeline";

const STATUS_COLUMNS = [
  { key: "draft", label: "Rascunho" },
  { key: "to-approve", label: "Aprovacao" },
  { key: "adjust", label: "Ajustes" },
  { key: "approved", label: "Aprovado" },
];

const PAGE_SIZE = 10;

type KanbanColumnProps = {
  statusKey: string;
  label: string;
  filters: Record<string, any>;
  groupByCustomer: boolean;
  onJobClick: (jobId: string) => void;
  resetKey: number;
};

const KanbanColumnV2 = ({ statusKey, label, filters, groupByCustomer, onJobClick, resetKey }: KanbanColumnProps) => {
  const [jobs, setJobs] = React.useState<any[]>([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [total, setTotal] = React.useState<number | null>(null);
  const pageRef = React.useRef(0);
  const scrollId = `kanban-scroll-${statusKey}`;

  const loadMore = React.useCallback(() => {
    GET_ALL_JOBS({ ...filters, statusKey, page: pageRef.current })
      .then((res) => {
        const items: any[] = res?.items ?? [];
        setJobs((prev) => (pageRef.current === 0 ? items : [...prev, ...items]));
        if (res?.total !== undefined) setTotal(res.total);
        setHasMore(items.length >= PAGE_SIZE);
        pageRef.current += 1;
      })
      .catch((err: any) => {
        showNotification({ title: "Ops", message: err?.message || "Falha ao carregar coluna", color: "red" });
        setHasMore(false);
      });
  }, [filters, statusKey]);

  React.useEffect(() => {
    setJobs([]);
    setHasMore(true);
    setTotal(null);
    pageRef.current = 0;
    loadMore();
  }, [resetKey, statusKey, JSON.stringify(filters)]);

  const groupedByCustomer = React.useMemo(() =>
    jobs.reduce<Record<string, { name: string; items: any[] }>>((acc, job) => {
      const key = job?.customer?._id || "sem-cliente";
      const name = job?.customer?.name || "Sem cliente";
      const prev = acc[key] || { name, items: [] };
      return { ...acc, [key]: { name: prev.name, items: [...prev.items, job] } };
    }, {})
  , [jobs]);

  return (
    <Card withBorder radius="md" p="sm" style={{ minHeight: 420 }}>
      <Group position="apart" mb="xs">
        <Text weight={700}>{label}</Text>
        <Badge color="gray" variant="filled">{total ?? jobs.length}</Badge>
      </Group>
      <div
        id={scrollId}
        style={{ height: 430, overflowY: "auto" }}
      >
        <InfiniteScroll
          dataLength={jobs.length}
          next={() => loadMore()}
          hasMore={hasMore}
          scrollableTarget={scrollId}
          loader={
            <Stack spacing={6} px={4}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} withBorder p="xs" style={{ opacity: 0.4 }}>
                  <Text size="sm" color="dimmed">Carregando...</Text>
                </Card>
              ))}
            </Stack>
          }
          endMessage={<Text size="xs" color="dimmed" align="center" py={4}></Text>}
        >
          {!groupByCustomer && (
            <Stack spacing={8}>
              {jobs.map((job) => (
                <Card key={job.id} withBorder p="xs" style={{ cursor: "pointer" }} onClick={() => onJobClick(job.id)}>
                  <Text size="sm" weight={600}>{job.name}</Text>
                  <Text size="xs" color="dimmed">{job?.customer?.name || "Sem cliente"}</Text>
                </Card>
              ))}
            </Stack>
          )}
          {groupByCustomer && (
            <Stack spacing={10}>
              {Object.entries(groupedByCustomer).map(([key, group]) => (
                <Card key={`${statusKey}-group-${key}`} withBorder p="xs">
                  <Text size="xs" color="dimmed" mb={6}>{group.name}</Text>
                  <Stack spacing={6}>
                    {group.items.map((job) => (
                      <Card key={job.id} withBorder p={6} style={{ cursor: "pointer" }} onClick={() => onJobClick(job.id)}>
                        <Text size="sm" weight={600}>{job.name}</Text>
                      </Card>
                    ))}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </InfiniteScroll>
      </div>
    </Card>
  );
};

const CommandCenterV2 = () => {
  const { customers, agents } = useApp();

  const [viewMode, setViewMode] = React.useState<ViewMode>("kanban");
  const [loading, setLoading] = React.useState(true);
  const [jobsByStatus, setJobsByStatus] = React.useState<{ [key: string]: any[] }>({});
  const [jobsTodayOverdue, setJobsTodayOverdue] = React.useState<any[]>([]);
  const [timelineItems, setTimelineItems] = React.useState<any[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<string | null>(null);
  const [selectedResponsibleId, setSelectedResponsibleId] = React.useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = React.useState<string | null>(null);
  const [dateStart, setDateStart] = React.useState<string>("");
  const [dateEnd, setDateEnd] = React.useState<string>("");
  const [groupByCustomer, setGroupByCustomer] = React.useState(false);
  const [kanbanResetKey, setKanbanResetKey] = React.useState(0);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedJobId, setSelectedJobId] = React.useState<string | null>(null);
  const [selectedJob, setSelectedJob] = React.useState<any>(null);
  const [loadingJob, setLoadingJob] = React.useState(false);
  const [statusOptions, setStatusOptions] = React.useState<any[]>([]);
  const [savingInline, setSavingInline] = React.useState(false);
  const [visualComments, setVisualComments] = React.useState<Array<{ x: number; y: number; text: string }>>([]);
  const [nextVisualComment, setNextVisualComment] = React.useState("");
  const [pendingVisualPoint, setPendingVisualPoint] = React.useState<{ x: number; y: number } | null>(null);

  const customerOptions = customers
    .filter((c) => c.active)
    .map((c) => ({ value: c._id, label: c.name }));

  const responsibleOptions = agents.map((a) => ({ value: a._id, label: a.name }));

  const baseFilters = React.useMemo(
    () => ({
      customerId: selectedCustomerId || undefined,
      agentId: selectedResponsibleId || undefined,
      ...(dateStart ? { deadlineStart: dateStart } : {}),
      ...(dateEnd ? { deadlineEnd: dateEnd } : {}),
      page: 0,
    }),
    [selectedCustomerId, selectedResponsibleId, dateStart, dateEnd]
  );

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [boards, allJobs, tasksData, options] = await Promise.all([
        Promise.all(
          STATUS_COLUMNS.map((column) =>
            GET_ALL_JOBS({ ...baseFilters, statusKey: column.key }).then((res) => ({ key: column.key, items: res?.items ?? [] }))
          )
        ),
        GET_ALL_JOBS(baseFilters),
        GET_ALL_TASKS_DESTINATIONS({
          customerId: selectedCustomerId || undefined,
          agentId: selectedResponsibleId || undefined,
          dateStart: dateStart || moment().startOf("month").toDate(),
          dateEnd: dateEnd || moment().endOf("month").toDate(),
        }),
        GET_JOB_STATUS_OPTIONS(),
      ]);

      const grouped = boards.reduce((acc, board) => ({ ...acc, [board.key]: board.items }), {});
      const rawJobs = allJobs?.items ?? [];

      const today = moment().endOf("day");
      const filteredToday = rawJobs
        .filter((job) => {
          if (!job?.deadline) return false;
          const m = moment(job.deadline);
          return m.isSame(today, "day") || m.isBefore(today);
        })
        .filter((job) => !selectedStatus || job.statusKey === selectedStatus)
        .slice(0, 8);

      const timeline = (tasksData ?? [])
        .flatMap((task) =>
          (task?.destinations ?? [])
            .filter((destination) => destination?.publish_date)
            .map((destination) => ({
              date: destination.publish_date,
              channel: destination.origin,
              jobId: task.jobId,
              taskTitle: task.title,
              customerName: task.customer?.name,
            }))
        )
        .sort((a, b) => (a.date > b.date ? 1 : -1));

      setJobsByStatus(grouped);
      setJobsTodayOverdue(filteredToday);
      setTimelineItems(timeline);
      setStatusOptions(options ?? []);
    } catch (err: any) {
      showNotification({
        title: "Ops",
        message: err?.message || "Nao foi possivel carregar o Command Center V2",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [baseFilters, selectedCustomerId, selectedResponsibleId, dateStart, dateEnd, selectedStatus]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const openJob = async (jobId: string) => {
    setSelectedJobId(jobId);
    setDrawerOpen(true);
    setLoadingJob(true);
    try {
      const details = await GET_JOB_DETAILS(jobId);
      setSelectedJob(details);
      setVisualComments([]);
      setNextVisualComment("");
      setPendingVisualPoint(null);
    } catch (err: any) {
      showNotification({ title: "Ops", message: err?.message || "Nao foi possivel abrir o job", color: "red" });
    } finally {
      setLoadingJob(false);
    }
  };

  const saveInline = async (partialData) => {
    if (!selectedJobId) return;
    setSavingInline(true);
    try {
      await SAVE_JOB({ id: selectedJobId, ...partialData });
      setSelectedJob((prev) => ({ ...prev, ...partialData }));
      showNotification({ message: "Atualizado", color: "green" });
      loadData();
    } catch (err: any) {
      showNotification({ title: "Ops", message: err?.message || "Falha ao salvar", color: "red" });
    } finally {
      setSavingInline(false);
    }
  };

  const changeStatusByKey = async (statusKey: string) => {
    if (!selectedJobId) return;
    const status = statusOptions.find((option) => option.key === statusKey);
    if (!status?._id) {
      showNotification({ title: "Ops", message: "Status nao encontrado", color: "yellow" });
      return;
    }

    setSavingInline(true);
    try {
      await SAVE_JOB_STATUS(selectedJobId, status._id);
      const details = await GET_JOB_DETAILS(selectedJobId);
      setSelectedJob(details);
      showNotification({ message: "Status atualizado", color: "green" });
      loadData();
    } catch (err: any) {
      showNotification({ title: "Ops", message: err?.message || "Falha ao alterar status", color: "red" });
    } finally {
      setSavingInline(false);
    }
  };

  const selectedStatusId = (selectedJob?.job_status || []).sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))[0]?.status?._id;
  const selectedChannels: string[] = Array.from(
    new Set<string>((selectedJob?.tasks || []).flatMap((task) => (task?.destinations || []).map((destination) => destination?.origin).filter(Boolean)))
  );

  const clientProgress = React.useMemo(() => {
    const allCards = STATUS_COLUMNS.flatMap((status) => jobsByStatus[status.key] || []);
    const grouped = allCards.reduce((acc, job) => {
      const key = job?.customer?._id || "sem-cliente";
      const name = job?.customer?.name || "Sem cliente";
      const current = acc[key] || { key, name, total: 0, approved: 0 };
      current.total += 1;
      if (job?.statusKey === "approved") current.approved += 1;
      return { ...acc, [key]: current };
    }, {});

    return Object.values(grouped);
  }, [jobsByStatus]);

  const focusModeStyle = drawerOpen
    ? { filter: "blur(1.5px)", opacity: 0.55, pointerEvents: "none" as const, transform: "scale(0.995)", transition: "all 180ms ease" }
    : { transition: "all 180ms ease" };

  return (
    <div style={{ position: "relative" }}>
      <Stack spacing="md" style={focusModeStyle}>
        <Card radius="xl" shadow="sm" p="md" style={{ position: "sticky", top: 0, zIndex: 5, border: "1px solid #e6ebf5", background: "linear-gradient(90deg, #ffffff, #f7faff)" }}>
          <Group align="end" spacing="sm" noWrap>
            <Group spacing={6}>
              <FaFilter color="#5f6f88" />
              <Text size="sm" color="dimmed">Filtros globais</Text>
            </Group>
            <Select
              value={selectedCustomerId}
              onChange={setSelectedCustomerId}
              placeholder="Cliente"
              data={customerOptions}
              clearable
              style={{ minWidth: 180 }}
            />
            <Select
              value={selectedResponsibleId}
              onChange={setSelectedResponsibleId}
              placeholder="Responsavel"
              data={responsibleOptions}
              clearable
              style={{ minWidth: 180 }}
            />
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Status"
              data={STATUS_COLUMNS.map((status) => ({ value: status.key, label: status.label }))}
              clearable
              style={{ minWidth: 160 }}
            />
            <TextInput type="date" value={dateStart} onChange={(e) => setDateStart(e.currentTarget.value)} style={{ minWidth: 150 }} />
            <TextInput type="date" value={dateEnd} onChange={(e) => setDateEnd(e.currentTarget.value)} style={{ minWidth: 150 }} />
            <Switch checked={groupByCustomer} onChange={(e) => setGroupByCustomer(e.currentTarget.checked)} label="Agrupar por cliente" />
            <Button onClick={() => { loadData(); setKanbanResetKey((k) => k + 1); }}>Aplicar</Button>
          </Group>
        </Card>

        {loading ? (
          <Group position="center" py="xl"><Loader /></Group>
        ) : (
          <>
            <SimpleGrid cols={2} breakpoints={[{ maxWidth: 1080, cols: 1 }]}>
              <Card radius="xl" p="md" shadow="sm" style={{ border: "1px solid #e8edf6" }}>
                <Group position="apart" mb="sm">
                  <Group spacing={8}><FaExclamationTriangle color="#d17f09" /><Title order={4}>Hoje / Atrasados</Title></Group>
                  <Badge color="orange" variant="light">{jobsTodayOverdue.length}</Badge>
                </Group>
                <Stack spacing={8}>
                  {jobsTodayOverdue.length === 0 && <Text color="dimmed" size="sm">Sem jobs nesse recorte.</Text>}
                  {jobsTodayOverdue.map((job) => (
                    <Card key={job.id} withBorder p="sm" style={{ cursor: "pointer" }} onClick={() => openJob(job.id)}>
                      <Group position="apart" align="flex-start">
                        <Box>
                          <Text weight={600}>{job.name}</Text>
                          <Text size="xs" color="dimmed">{job?.customer?.name || "Sem cliente"}</Text>
                        </Box>
                        <Group spacing={4}><FaClock size={12} /><Text size="xs">{moment(job.deadline).format("DD/MM")}</Text></Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Card>

              <Card radius="xl" p="md" shadow="sm" style={{ border: "1px solid #e8edf6" }}>
                <Group position="apart" mb="sm">
                  <Group spacing={8}><FaUserTie color="#3c5f96" /><Title order={4}>Clientes</Title></Group>
                  <Badge color="blue" variant="light">Resumo</Badge>
                </Group>
                <Stack spacing={10}>
                  {clientProgress.length === 0 && <Text color="dimmed" size="sm">Nenhum cliente com jobs.</Text>}
                  {clientProgress.slice(0, 8).map((client: any) => (
                    <Group key={client.key} position="apart">
                      <Text size="sm">{client.name}</Text>
                      <Badge color="green" variant="outline">{client.approved}/{client.total} aprovados</Badge>
                    </Group>
                  ))}
                </Stack>
              </Card>
            </SimpleGrid>

            <Card radius="xl" p="md" shadow="sm" style={{ border: "1px solid #e8edf6" }}>
              <Group position="apart" mb="sm">
                <Group spacing={8}><FaColumns color="#3f6078" /><Title order={4}>Operacao Unificada</Title></Group>
                <Group spacing={6}>
                  <Button variant={viewMode === "kanban" ? "filled" : "light"} leftIcon={<FaColumns />} onClick={() => setViewMode("kanban")}>Kanban</Button>
                  <Button variant={viewMode === "timeline" ? "filled" : "light"} leftIcon={<FaCalendarAlt />} onClick={() => setViewMode("timeline")}>Timeline</Button>
                </Group>
              </Group>

              {viewMode === "kanban" && (
                <SimpleGrid cols={4} breakpoints={[{ maxWidth: 1200, cols: 2 }, { maxWidth: 780, cols: 1 }]}>
                  {STATUS_COLUMNS
                    .filter((column) => !selectedStatus || column.key === selectedStatus)
                    .map((column) => (
                      <KanbanColumnV2
                        key={column.key}
                        statusKey={column.key}
                        label={column.label}
                        filters={baseFilters}
                        groupByCustomer={groupByCustomer}
                        onJobClick={openJob}
                        resetKey={kanbanResetKey}
                      />
                    ))
                  }
                </SimpleGrid>
              )}

              {viewMode === "timeline" && (
                <Stack spacing={8}>
                  {timelineItems.length === 0 && <Text color="dimmed">Nenhum agendamento no periodo.</Text>}
                  {timelineItems.map((item, idx) => (
                    <Card key={`${item.jobId}-${idx}`} withBorder p="sm" style={{ cursor: "pointer" }} onClick={() => openJob(item.jobId)}>
                      <Group position="apart" noWrap>
                        <Group spacing={8}>
                          <FaRegCalendarCheck color="#3a5f8d" />
                          <div>
                            <Text size="sm" weight={600}>{item.taskTitle}</Text>
                            <Text size="xs" color="dimmed">{item.customerName || "Sem cliente"} • {item.channel}</Text>
                          </div>
                        </Group>
                        <Badge color="blue" variant="light">{moment(item.date).format("DD/MM HH:mm")}</Badge>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              )}
            </Card>
          </>
        )}
      </Stack>

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="right"
        size="xl"
        title="Painel do Job"
        padding="md"
      >
        {loadingJob && <Group position="center" py="xl"><Loader /></Group>}
        {!loadingJob && !selectedJob && <Text color="dimmed">Selecione um job para abrir o contexto.</Text>}
        {!loadingJob && selectedJob && (
          <Stack spacing="md">
            <Card withBorder>
              <Group position="apart" align="start">
                <Box style={{ flex: 1 }}>
                  <TextInput
                    icon={<FaEdit />}
                    label="Nome do job"
                    value={selectedJob?.name || ""}
                    onChange={(event) => setSelectedJob((prev) => ({ ...prev, name: event.currentTarget.value }))}
                    onBlur={() => saveInline({ name: selectedJob?.name })}
                  />
                  <Text size="xs" color="dimmed" mt={4}>{selectedJob?.customer?.name || "Sem cliente"}</Text>
                </Box>
                <Select
                  label="Status"
                  value={selectedStatusId}
                  onChange={async (statusId) => {
                    if (!statusId || !selectedJobId) return;
                    setSavingInline(true);
                    await SAVE_JOB_STATUS(selectedJobId, statusId);
                    const details = await GET_JOB_DETAILS(selectedJobId);
                    setSelectedJob(details);
                    setSavingInline(false);
                    loadData();
                  }}
                  data={(statusOptions || []).map((status) => ({ value: status._id, label: status.title }))}
                  style={{ minWidth: 220 }}
                />
              </Group>
            </Card>

            <SimpleGrid cols={2} breakpoints={[{ maxWidth: 980, cols: 1 }]}>
              <Stack spacing="md">
                <Card withBorder>
                  <Group spacing={8} mb="xs"><FaEdit /><Text weight={600}>Conteudo</Text></Group>
                  <Textarea
                    minRows={6}
                    value={selectedJob?.description || ""}
                    onChange={(event) => setSelectedJob((prev) => ({ ...prev, description: event.currentTarget.value }))}
                    onBlur={() => saveInline({ description: selectedJob?.description || "" })}
                    placeholder="Edicao inline do copy"
                  />
                </Card>

                <Card withBorder>
                  <Group spacing={8} mb="xs"><FaImages /><Text weight={600}>Midia</Text></Group>
                  <Box
                    style={{
                      border: "1px dashed #b8c5d9",
                      borderRadius: 12,
                      padding: 16,
                      textAlign: "center",
                      background: "#f8fbff",
                    }}
                  >
                    <Text size="sm" color="dimmed">Upload drag and drop (v2 base)</Text>
                    <Text size="xs" color="dimmed">A integracao final de upload pode reutilizar o fluxo de arquivos legados.</Text>
                  </Box>
                </Card>

                <Card withBorder>
                  <Group spacing={8} mb="xs"><FaCommentDots /><Text weight={600}>Comentarios</Text></Group>
                  <Stack spacing={8}>
                    {(selectedJob?.adjustment || []).slice(-5).map((comment, index) => (
                      <Card key={`comment-${index}`} p="xs" style={{ background: "#f6f9ff" }}>
                        <Text size="xs" color="dimmed">{comment?.approver?.name || comment?.createdBy?.name || "Usuario"}</Text>
                        <Text size="sm">{comment?.text || "Sem texto"}</Text>
                      </Card>
                    ))}
                    {(selectedJob?.adjustment || []).length === 0 && <Text size="sm" color="dimmed">Sem comentarios ainda.</Text>}
                  </Stack>
                </Card>

                <Card withBorder>
                  <Group spacing={8} mb="xs"><FaClock /><Text weight={600}>Historico</Text></Group>
                  <Stack spacing={8}>
                    {(selectedJob?.job_status || []).slice(0, 8).map((history, index) => (
                      <Group key={`history-${index}`} position="apart">
                        <Text size="sm">{history?.status?.title || "Atualizacao"}</Text>
                        <Text size="xs" color="dimmed">{moment(history?.createdAt).format("DD/MM HH:mm")}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Card>
              </Stack>

              <Stack spacing="md">
                <Card withBorder>
                  <Title order={5} mb="sm">Dados Operacionais</Title>
                  <Stack spacing="sm">
                    <TextInput
                      type="date"
                      label="Deadline"
                      value={selectedJob?.deadline ? moment(selectedJob.deadline).format("YYYY-MM-DD") : ""}
                      onChange={(event) => {
                        const value = event.currentTarget.value;
                        setSelectedJob((prev) => ({ ...prev, deadline: value ? moment(value).toDate() : null }));
                      }}
                      onBlur={() => saveInline({ deadline: selectedJob?.deadline || null })}
                    />
                    <Select
                      label="Responsavel"
                      placeholder="Selecione"
                      value={selectedJob?.responsible?._id || null}
                      onChange={(value) => {
                        const responsible = agents.find((agent) => agent._id === value);
                        setSelectedJob((prev) => ({ ...prev, responsible }));
                        if (responsible) {
                          saveInline({ responsible: { _id: responsible._id, name: responsible.name, image: responsible.image } });
                        }
                      }}
                      data={responsibleOptions}
                    />
                    <Group spacing={6}>
                      {selectedChannels.length === 0 && <Badge color="gray" variant="light">Sem canal</Badge>}
                      {selectedChannels.map((channel) => <Badge key={channel} color="blue" variant="outline">{channel}</Badge>)}
                    </Group>
                  </Stack>
                  <Divider my="md" />
                  <Stack>
                    <Button loading={savingInline} color="green" leftIcon={<FaCheck />} onClick={() => changeStatusByKey("approved")}>Aprovar</Button>
                    <Button loading={savingInline} color="yellow" variant="light" onClick={() => changeStatusByKey("adjust")}>Solicitar ajustes</Button>
                    <Button
                      loading={savingInline}
                      color="blue"
                      variant="outline"
                      onClick={() => saveInline({ deadline: moment().add(2, "days").toDate() })}
                    >
                      Agendar (D+2)
                    </Button>
                  </Stack>
                </Card>

                <Card withBorder>
                  <Title order={5} mb="sm">Modo Aprovacao Visual</Title>
                  <Text size="xs" color="dimmed" mb="sm">Clique na peca para marcar um comentario contextual.</Text>
                  <Box
                    onClick={(event) => {
                      const target = event.currentTarget.getBoundingClientRect();
                      const x = Math.max(0, Math.min(100, ((event.clientX - target.left) / target.width) * 100));
                      const y = Math.max(0, Math.min(100, ((event.clientY - target.top) / target.height) * 100));
                      setPendingVisualPoint({ x, y });
                    }}
                    style={{
                      position: "relative",
                      borderRadius: 16,
                      minHeight: 340,
                      background: "linear-gradient(160deg, #1f2d3d, #3a5f8d)",
                      color: "#fff",
                      overflow: "hidden",
                      padding: 20,
                      cursor: "crosshair",
                    }}
                  >
                    <Text size="xs" color="#d9e6ff">Preview Instagram</Text>
                    <Title order={4} mt={8}>{selectedJob?.name || "Post sem titulo"}</Title>
                    <Text mt="sm" size="sm" style={{ maxWidth: 340 }}>
                      {(selectedJob?.description || "Sem copy definido para este job.").slice(0, 180)}
                    </Text>
                    {visualComments.map((comment, index) => (
                      <Box
                        key={`v-comment-${index}`}
                        style={{
                          position: "absolute",
                          left: `${comment.x}%`,
                          top: `${comment.y}%`,
                          width: 22,
                          height: 22,
                          borderRadius: 999,
                          background: "#ffd43b",
                          border: "2px solid #111",
                          color: "#111",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                          transform: "translate(-50%, -50%)",
                        }}
                        title={comment.text}
                      >
                        {index + 1}
                      </Box>
                    ))}
                  </Box>
                  <Group mt="sm" align="end" noWrap>
                    <TextInput
                      placeholder={pendingVisualPoint ? "Digite o comentario para este ponto" : "Clique no preview para escolher o ponto"}
                      value={nextVisualComment}
                      onChange={(event) => setNextVisualComment(event.currentTarget.value)}
                      style={{ flex: 1 }}
                    />
                    <Button
                      disabled={!pendingVisualPoint || !nextVisualComment.trim()}
                      onClick={() => {
                        if (!pendingVisualPoint || !nextVisualComment.trim()) return;
                        setVisualComments((prev) => [...prev, { ...pendingVisualPoint, text: nextVisualComment.trim() }]);
                        setNextVisualComment("");
                        setPendingVisualPoint(null);
                      }}
                    >
                      Comentar na peca
                    </Button>
                  </Group>
                  <Stack spacing={6} mt="sm">
                    {visualComments.length === 0 && <Text size="xs" color="dimmed">Sem comentarios visuais ainda.</Text>}
                    {visualComments.map((comment, index) => (
                      <Card key={`visual-list-${index}`} p={8} style={{ background: "#f6f8ff" }}>
                        <Text size="xs" color="dimmed">Ponto {index + 1} ({Math.round(comment.x)}%, {Math.round(comment.y)}%)</Text>
                        <Text size="sm">{comment.text}</Text>
                      </Card>
                    ))}
                  </Stack>
                </Card>
              </Stack>
            </SimpleGrid>
          </Stack>
        )}
      </Drawer>
    </div>
  );
};

export default CommandCenterV2;